import React, { useState, useEffect, useRef } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { backendClient } from '@/lib/api-client';
import { Loading } from '../shared';
import { isVersionedDocumentId } from './imanageFolderPickerUtils';

interface IManageFolderPickerProps {
  isOpen: boolean;
  onClose: () => void;
  onFolderSelected?: (folder: any) => void;
  authData: {
    access_token: string;
    customer_id: string;
    user_id?: string;
    dialog_token?: string;
  };
}

// iManage's browse dialog reports BOTH the highlighted item (`selectedId`, which can be a
// DOCUMENT in browse mode) and the folder currently open (`location`/loc). For a folder
// sync we must resolve a real folder container id: prefer a version-less selected id
// (an actual folder), otherwise fall back to the browsed folder location. Returns null
// when neither is a usable folder id (e.g. a document was highlighted with no folder
// location) so the caller can surface an error rather than storing a document id.
const resolveFolderContainerId = (
  selectedId: string | undefined | null,
  location: string | undefined | null,
): string | null => {
  if (selectedId && selectedId.includes('!') && !isVersionedDocumentId(selectedId)) {
    return selectedId;
  }
  if (location && location.includes('!') && !isVersionedDocumentId(location)) {
    return location;
  }
  return null;
};

const SELECT_A_FOLDER_MESSAGE =
  'That is a document, not a folder. Please open or select a folder to sync.';

export const IManageFolderPicker: React.FC<IManageFolderPickerProps> = ({
  isOpen,
  onClose,
  onFolderSelected,
  authData,
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [iframeUrl, setIframeUrl] = useState<string | null>(null);
  const [isLoadingPicker, setIsLoadingPicker] = useState(false);
  const [selectionError, setSelectionError] = useState<string | null>(null);

  // Load folder picker config when dialog opens
  useEffect(() => {
    if (isOpen && authData?.access_token && authData?.customer_id) {
      loadFolderPicker();
    }
  }, [isOpen, authData]);

  const loadFolderPicker = async () => {
    setSelectionError(null);
    setIsLoadingPicker(true);
    try {
      // Get the fully-configured folder picker URL from backend
      const response = await backendClient.get('/imanage/folder-picker-config', {
        params: {
          access_token: authData.access_token,
          customer_id: authData.customer_id,
          user_id: authData.user_id,
        },
      });

      setIframeUrl(response.data.iframe_url);
    } catch (error: any) {
      console.error('Failed to load folder picker:', error);
    } finally {
      setIsLoadingPicker(false);
    }
  };

  // Handle postMessage from iManage iframe
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Verify the message is from iManage (support subdomains with regex)
      if (!event.origin.match(/^https:\/\/([\w-]+\.)?cloudimanage\.com$/)) {
        return;
      }

      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;

        if (data.type === 'ready') {
          // Folder picker is ready
        } else if (data.type === 'action') {
          // Custom action button clicked (e.g., "Select Folder")
          const folderLocation = data.loc || data.data?.loc;
          const selected = data.selected || data.data?.selected || data.data?.items || data.items || [];

          // Resolve a real folder container id: the action's `loc` is the folder the user
          // is in, but fall back through the resolver so a versioned/document value never
          // slips through as the sync root.
          const containerId = resolveFolderContainerId(folderLocation, folderLocation);
          if (containerId) {
            const parts = containerId.split('!');
            const database = parts[0] || 'Unknown';
            const folderId = parts[1] || containerId;

            const folderData = {
              ...data.output_data,
              id: containerId,
              wsid: folderId,
              name: data.output_data?.name || folderId,
              database: database,
              path: containerId,
              document_count: 0,
            };

            setSelectionError(null);
            if (onFolderSelected) {
              onFolderSelected(folderData);
            }
            onClose();
          } else if (selected && selected.length > 0) {
            // Fallback to selected items
            const folder = selected[0];
            const fallbackId = resolveFolderContainerId(folder?.id, folder?.loc || folderLocation);
            if (!fallbackId) {
              setSelectionError(SELECT_A_FOLDER_MESSAGE);
              return;
            }
            setSelectionError(null);
            if (onFolderSelected) {
              onFolderSelected({ ...folder, id: fallbackId, wsid: fallbackId.split('!')[1] || fallbackId });
            }
            onClose();
          } else if (folderLocation) {
            // A location was provided but it was not a valid folder id (e.g. a document).
            setSelectionError(SELECT_A_FOLDER_MESSAGE);
          }
        } else if (data.type === 'open') {
          // In browse mode, "Open Folder" is actually the selection action
          // In browse mode, data.data contains the folder ID being "opened" (i.e., selected)
          // Format: 'DEV!19' (library!folderID)
          const selectedId = typeof data.data === 'string' ? data.data : data.selected;
          const location = data.loc;
          const outputData = data.output_data;

          if (selectedId && typeof selectedId === 'string') {
            // Resolve a real folder container id, falling back to the browsed folder
            // location when the highlighted item is a document.
            const containerId = resolveFolderContainerId(selectedId, location);
            if (!containerId) {
              setSelectionError(SELECT_A_FOLDER_MESSAGE);
              return;
            }
            const parts = containerId.split('!');
            const database = parts[0] || 'Unknown';
            const folderId = parts[1] || containerId;
            const selectedDocument = isVersionedDocumentId(selectedId);
            const locationData =
              selectedDocument && outputData?.loc && typeof outputData.loc === 'object'
                ? outputData.loc
                : null;
            const workspaceId = locationData?.workspace_id || outputData?.workspace_id;
            if (selectedDocument && !workspaceId) {
              setSelectionError(SELECT_A_FOLDER_MESSAGE);
              return;
            }
            // When a document selection falls back to its containing folder, do not
            // carry the document's name onto the folder sync. Workspace metadata is
            // safe and required to construct the normalized iManage folder id.
            const folderOutputData = selectedDocument ? locationData : outputData;

            const folderData = {
              ...folderOutputData,
              id: containerId,
              wsid: folderId,
              name: folderOutputData?.name || folderId,
              database: database,
              path: location || containerId,
              document_count: 0,
              workspace_id: workspaceId,
              workspace_name: locationData?.workspace_name || outputData?.workspace_name,
            };

            setSelectionError(null);
            if (onFolderSelected) {
              onFolderSelected(folderData);
            }
            onClose();
          }
        } else if (data.type === 'select' || data.type === 'save') {
          // File/folder selected
          // iManage postMessage can have different structures:
          // The actual event data is in data.data, not at the root level
          const eventData = data.data || data;

          const selectedId = eventData.selected || data.selected;
          const location = eventData.loc || data.loc; // Folder/workspace location like 'DEV!19'
          const outputData = eventData.output_data || data.output_data;

          if (selectedId) {
            // The selected id can be a DOCUMENT ('LIBRARY!DOCNUM.VERSION'). Resolve a real
            // folder container id: a version-less selected id is a folder, otherwise fall
            // back to the folder location (`loc`). Never store a document id as the root.
            const containerId = resolveFolderContainerId(selectedId, location);
            if (!containerId) {
              setSelectionError(SELECT_A_FOLDER_MESSAGE);
              return;
            }
            const parts = containerId.split('!');
            const database = parts[0] || 'Unknown';
            const idPart = parts[1] || containerId;

            // Extract name/metadata from the folder location entry where possible.
            const locData = outputData?.loc || outputData?.selected?.[0] || {};
            const folderName = locData?.name || outputData?.loc?.name || idPart;

            // Try multiple possible field names for document count
            const docCount = locData.document_count
              || locData.item_count
              || locData.size
              || (locData.has_documents ? null : 0); // null means "unknown count but has docs"

            const folderData = {
              id: containerId,
              wsid: idPart,
              name: folderName,
              database: database,
              path: location || containerId,
              document_count: docCount,
              // Include metadata from locData (not outputData which contains nested structures)
              workspace_name: locData.workspace_name,
              workspace_id: locData.workspace_id,
              folder_type: locData.folder_type,
              has_documents: locData.has_documents,
              has_subfolders: locData.has_subfolders,
              owner: locData.owner,
              owner_description: locData.owner_description
            };

            setSelectionError(null);
            if (onFolderSelected) {
              onFolderSelected(folderData);
            }
            onClose();
          }
        } else if (data.type === 'cancel') {
          // User cancelled the selection
          onClose();
        }
      } catch (error) {
        console.error('Error handling postMessage:', error);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [onFolderSelected, onClose]);

  const renderContent = () => {
    if (isLoadingPicker) {
      return (
        <div className="flex items-center justify-center h-[80vh]">
          <Loading text='Loading iManage folder picker' />
        </div>
      );
    }

    if (iframeUrl) {
      return (
        <iframe
          ref={iframeRef}
          src={iframeUrl}
          className="w-full h-[95vh] border-0"
          title="iManage Folder Picker"
          sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
        />
      );
    }

    return null;
  };

  return (
    <>
      <DialogPrimitive.Root open={isOpen} onOpenChange={onClose}>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay
            className="fixed inset-0 z-100000 bg-black/10 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
          />
          <DialogPrimitive.Content
            className="fixed left-[50%] top-[50%] z-100001 max-w-[95vw] w-[95vw] max-h-[95vh] h-[95vh] translate-x-[-50%] translate-y-[-50%] border border-stone-200 bg-white shadow-2xl duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 rounded-lg overflow-hidden p-0"
          >
            {selectionError && (
              <div className="absolute left-1/2 top-3 z-20 -translate-x-1/2 rounded-md border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-800 shadow-md">
                {selectionError}
              </div>
            )}
            {renderContent()}
            <DialogPrimitive.Close className="absolute right-3 top-3 z-10 rounded-xs p-1.5 text-gray-500 transition-all hover:bg-gray-100 hover:text-gray-700 focus:outline-hidden focus:ring-2 focus:ring-gray-400 focus:ring-offset-2">
              <X className="h-5 w-5" />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
    </>
  );
};
